'use memo'

// web/src/features/event/EventContext.tsx
import { Formik, type FormikHelpers } from 'formik'
import { message } from 'antd'
import { submitEvent } from '@sportninja/common/features/event/buildEventSubmit'
import { eventToFormValues } from '@sportninja/common/features/event/eventToFormValues'
import { useEventMutations } from '@sportninja/common/features/event/hooks/useEventMutations'
import type {
  EventFormValues,
  SnEvent,
} from '@sportninja/common/features/event/types'

interface EventFormProviderProps {
  event: SnEvent | null
  /** Absent for a schedule-owned event, which belongs to no team. */
  teamId?: string
  /** Required to create a team event; not needed when only editing/deleting. */
  rosterId?: string
  /**
   * Set instead of `teamId`/`rosterId` to create an event owned by a schedule
   * (a competition, season or division) rather than by a team.
   */
  scheduleId?: string
  /**
   * The authoring admin's own account timezone (or the browser zone as a
   * fallback) — seeds the form's non-visible `timezone` field.
   */
  defaultTimezone?: string
  onSaved: (saved: SnEvent) => void
  children: React.ReactNode
}

/**
 * Formik provider for the event add/edit form.
 *
 * Validation is intentionally off (`validateOnChange`/`validateOnBlur` false and
 * no validation schema) — every field error comes from the API response and is
 * applied through applyEventApiError (via submitEvent).
 */
export const EventFormProvider: React.FC<EventFormProviderProps> = ({
  event,
  teamId,
  rosterId,
  scheduleId,
  defaultTimezone,
  onSaved,
  children,
}) => {
  const { createEvent, updateEvent } = useEventMutations({
    teamId,
    rosterId,
    scheduleId,
  })
  const initialValues = eventToFormValues(event, defaultTimezone)

  const handleSubmit = (
    values: EventFormValues,
    helpers: FormikHelpers<EventFormValues>
  ) =>
    submitEvent(
      values,
      {
        setErrors: helpers.setErrors,
        setTouched: helpers.setTouched,
      },
      {
        event,
        teamId,
        rosterId,
        scheduleId,
        createEvent,
        updateEvent,
        notifySuccess: (msg) => message.success(msg),
        notifyError: (msg) => message.error(msg),
        onSaved,
      }
    )

  return (
    <Formik<EventFormValues>
      initialValues={initialValues}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleSubmit}
    >
      {children}
    </Formik>
  )
}
